import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IonButton, IonInput, IonText } from '@ionic/angular/standalone';

import { SudoPromptService } from '../../core/services/sudo-prompt.service';

/**
 * MOB-100 Stage 18 — modal sudo prompt overlay.
 *
 * Renders only when `SudoPromptService.request()` is non-null. Mounted
 * once in the root `<App>` component so it can fly in over any active
 * page. The HTTP `sudoInterceptor` opens it on `403 sudo-required` and
 * awaits the resolution promise before retrying the original request.
 *
 * Symmetrical with `apps/web` Stage 15B prompt, with two mobile-only
 * additions:
 *  - When `request().biometricAvailable === true` we surface a primary
 *    "Use Face ID / fingerprint" button that calls
 *    `SudoPromptService.submitBiometric()`. Declined biometric falls
 *    back to the password field automatically (the button's
 *    `'biometric-declined'` throw is caught and surfaced as a soft hint).
 *  - When the server returns `429 sudo-locked` the modal switches to a
 *    read-only lockout banner per the prompt's edge case.
 */
@Component({
  selector: 'mob-sudo-prompt',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, IonButton, IonInput, IonText],
  template: `
    @if (service.request(); as req) {
      <div class="mob-sudo__backdrop" (click)="cancel()" aria-hidden="true"></div>
      <div
        class="mob-sudo__container"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mob-sudo-title"
      >
        <div class="mob-sudo__content">
          <h2 id="mob-sudo-title" class="mob-sudo__title">Confirm your identity</h2>
          <p class="mob-sudo__subtitle">
            This action requires re-authentication. The permission stays active for 30 minutes.
          </p>
          @if (req.triggeringAction) {
            <p class="mob-sudo__action">
              <code>{{ req.triggeringAction }}</code>
            </p>
          }

          @if (lockoutMessage()) {
            <div class="mob-sudo__error mob-sudo__error--block" role="alert">
              {{ lockoutMessage() }}
            </div>
            <ion-button expand="block" shape="round" fill="outline" (click)="cancel()">
              Close
            </ion-button>
          } @else {
            @if (req.biometricAvailable) {
              <ion-button
                expand="block"
                shape="round"
                (click)="useBiometric()"
                [disabled]="busy()"
              >
                {{ busy() && lastChannel() === 'biometric' ? 'Verifying…' : 'Use Face ID / fingerprint' }}
              </ion-button>
            }

            <form (submit)="onSubmit($event)" novalidate>
              <label class="mob-sudo__label" for="mob-sudo-password">Password</label>
              <ion-input
                id="mob-sudo-password"
                type="password"
                autocomplete="current-password"
                [(ngModel)]="password"
                name="sudoPassword"
                [disabled]="busy()"
                fill="outline"
              />

              @if (errorMessage()) {
                <ion-text color="danger" class="mob-sudo__error" role="alert">
                  {{ errorMessage() }}
                </ion-text>
              }

              <div class="mob-sudo__actions">
                <ion-button shape="round" fill="clear" (click)="cancel()" [disabled]="busy()">
                  Cancel
                </ion-button>
                <ion-button shape="round" type="submit" [disabled]="busy() || !password">
                  {{ busy() && lastChannel() === 'password' ? 'Verifying…' : 'Confirm' }}
                </ion-button>
              </div>
            </form>
          }
        </div>
      </div>
    }
  `,
  styles: [
    `
      .mob-sudo__backdrop {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.5);
        backdrop-filter: blur(4px);
        z-index: 1100;
      }
      .mob-sudo__container {
        position: fixed;
        inset: 0;
        display: flex;
        align-items: flex-end;
        justify-content: center;
        z-index: 1101;
        padding: env(safe-area-inset-bottom, 0) 0;
      }
      .mob-sudo__content {
        background: var(--vevents-color-bg, #ffffff);
        color: var(--vevents-color-text, #0f172a);
        width: 100%;
        max-width: 32rem;
        border-radius: 18px 18px 0 0;
        padding: 1.5rem 1.25rem calc(1.25rem + env(safe-area-inset-bottom, 0));
        box-shadow: 0 -8px 32px rgba(0, 0, 0, 0.18);
        animation: mob-sudo-slide 0.24s ease-out;
      }
      .mob-sudo__title {
        margin: 0 0 0.4rem;
        font-size: 1.15rem;
      }
      .mob-sudo__subtitle {
        margin: 0 0 0.75rem;
        opacity: 0.78;
        font-size: 0.9rem;
      }
      .mob-sudo__action {
        margin: 0 0 1rem;
        font-size: 0.75rem;
        opacity: 0.7;
      }
      .mob-sudo__action code {
        background: rgba(15, 23, 42, 0.08);
        padding: 0.1rem 0.4rem;
        border-radius: 6px;
      }
      .mob-sudo__label {
        display: block;
        font-size: 0.85rem;
        font-weight: 500;
        margin: 0.5rem 0 0.25rem;
      }
      .mob-sudo__error {
        display: block;
        font-size: 0.85rem;
        margin-top: 0.4rem;
      }
      .mob-sudo__error--block {
        text-align: center;
        margin: 0 0 1rem;
      }
      .mob-sudo__actions {
        display: flex;
        gap: 0.5rem;
        justify-content: flex-end;
        margin-top: 1rem;
      }
      @keyframes mob-sudo-slide {
        from {
          transform: translateY(40%);
          opacity: 0;
        }
        to {
          transform: translateY(0);
          opacity: 1;
        }
      }
    `
  ]
})
export class SudoPromptComponent {
  protected readonly service = inject(SudoPromptService);

  password = '';
  readonly busy = signal(false);
  readonly lastChannel = signal<'biometric' | 'password' | null>(null);
  readonly errorMessage = signal<string | null>(null);

  readonly lockoutMessage = computed(() => this.service.request()?.lockoutMessage ?? null);

  async onSubmit(event: Event): Promise<void> {
    event.preventDefault();
    if (this.busy() || !this.password) return;
    this.busy.set(true);
    this.lastChannel.set('password');
    this.errorMessage.set(null);
    try {
      await this.service.submitPassword(this.password);
      this.password = '';
    } catch (err) {
      this.errorMessage.set(toMessage(err));
    } finally {
      this.busy.set(false);
    }
  }

  async useBiometric(): Promise<void> {
    if (this.busy()) return;
    this.busy.set(true);
    this.lastChannel.set('biometric');
    this.errorMessage.set(null);
    try {
      await this.service.submitBiometric();
    } catch (err) {
      if (err instanceof Error && err.message === 'biometric-declined') {
        this.errorMessage.set('Biometric verification declined. Enter your password to continue.');
      } else {
        this.errorMessage.set(toMessage(err));
      }
    } finally {
      this.busy.set(false);
    }
  }

  cancel(): void {
    this.password = '';
    this.errorMessage.set(null);
    this.service.cancel();
  }
}

function toMessage(err: unknown): string {
  if (typeof err === 'string') return err;
  if (err && typeof err === 'object' && 'message' in err) {
    return String((err as { message: unknown }).message);
  }
  return 'Verification failed. Please try again.';
}
